import React, { useRef } from 'react';

const OtpInput = ({
    length = 6,
    value = '',
    onChange,
    onComplete,
    autoFocus = true,
    disabled = false,
    error = false,
}: any) => {
    const inputsRef: any = useRef([]);

    const digits = value
        .replace(/[^0-9]/g, '')
        .slice(0, length)
        .split('')
        .concat(Array(length).fill(''))
        .slice(0, length);

    const focusInput = (index: any) => {
        const el: any = inputsRef.current[index];
        if (el) {
            el.focus();
            el.select?.();
        }
    };

    const emit = (nextDigits: any) => {
        const joined = nextDigits.join('');
        onChange?.(joined);
        if (nextDigits.every((d: any) => d !== '')) {
            onComplete?.(joined);
        }
    };

    const setDigit = (index: any, digit: any) => {
        const next = [...digits];
        next[index] = digit;
        emit(next);
    };

    // Shared by paste AND multi-char autofill-into-one-box cases.
    const distributeFrom = (startIndex: any, numStr: any) => {
        const next = [...digits];
        let cursor = startIndex;
        for (let i = 0; i < numStr.length && cursor < length; i++, cursor++) {
            next[cursor] = numStr[i];
        }
        emit(next);

        const nextEmptyIndex = next.findIndex((d) => d === '');
        focusInput(nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex);
    };

    const handleChange = (e: any, index: any) => {
        const onlyNums = e.target.value.replace(/[^0-9]/g, '');

        if (!onlyNums) {
            setDigit(index, '');
            return;
        }

        if (onlyNums.length > 1) {
            // More than one digit landed in a single box — typically
            // mobile SMS autofill delivering the full code at once.
            distributeFrom(index, onlyNums);
            return;
        }

        setDigit(index, onlyNums);
        if (index < length - 1) focusInput(index + 1);
    };

    const handleKeyDown = (e: any, index: any) => {
        const { key } = e;

        if (key === 'Backspace') {
            e.preventDefault();
            if (digits[index]) {
                setDigit(index, '');
            } else if (index > 0) {
                setDigit(index - 1, '');
                focusInput(index - 1);
            }
            return;
        }

        if (key === 'Delete') {
            e.preventDefault();
            setDigit(index, '');
            return;
        }

        if (key === 'ArrowLeft') {
            if (index > 0) { e.preventDefault(); focusInput(index - 1); }
            return;
        }

        if (key === 'ArrowRight') {
            if (index < length - 1) { e.preventDefault(); focusInput(index + 1); }
            return;
        }

        // Allow control/navigation keys and copy shortcuts through;
        // block every other non-numeric printable key.
        const isControlKey = key === 'Tab' || key === 'Enter' || key === 'Escape' || e.metaKey || e.ctrlKey;
        if (!isControlKey && key.length === 1 && !/[0-9]/.test(key)) {
            e.preventDefault();
        }
    };

    const handlePaste = (e: any, index: any) => {
        const globalWindow: any = window
        e.preventDefault();
        const pasted = (e.clipboardData || globalWindow.clipboardData).getData('text');
        const onlyNums = pasted.replace(/[^0-9]/g, '').slice(0, length - index || length);
        if (!onlyNums) return;
        distributeFrom(index, onlyNums);
    };

    return (
        <div className={`otp-input${error ? ' otp-input--error' : ''}${disabled ? ' otp-input--disabled' : ''}`}>
            {digits.map((digit: any, i: any) => (
                <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    className="otp-input__box"
                    value={digit}
                    disabled={disabled}
                    autoFocus={autoFocus && i === 0}
                    onChange={(e) => handleChange(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    onPaste={(e) => handlePaste(e, i)}
                    onFocus={(e) => e.target.select()}
                    aria-label={`Digit ${i + 1} of ${length}`}
                />
            ))}
        </div>
    );
};

export default OtpInput;